// !WARNING! Do not import store directly from @/store.
import { getAuthHeader } from '@/utils/Helpers'

import { isLocalId } from '@/utils/Id'
// Use `window.fpm.$store` instead. Otherwise u will get circular dependency
import axios from 'axios'
import qs from 'qs'
import pagerExtractor from './PagerExtractor'

export const BASE_URL = `/api/v1`

/**
 * Base request config
 * @type {AxiosInstance}
 */
export const HTTP = axios.create({
  baseURL: `${BASE_URL}`,
  paramsSerializer: function(params) {
    return qs.stringify(params, { arrayFormat: 'brackets' })
  }
})

/**
 * Tokenize each API request & prevent IE caching
 */
HTTP.interceptors.request.use(function(config) {
  config.headers = Object.assign(config.headers, getAuthHeader())

  // prevent IE caching
  // if (!config.params) config.params = {}
  // config.params._t = Date.now()

  return config
})

/**
 * Notify user about errors
 * @param error
 * @private
 */
const _handleError = (error) => {
  if (axios.isCancel(error)) {
    console.info('Request canceled', error.message)
    throw error
  }

  if (error.response) {
    switch (error.response.status) {
      case 500:
        window.fpm.$appToast.error('Server internal error.', 'Error 500!')
        break
      case 403:
        window.fpm.$appToast.error("You don't enough permissions for this action.", 'Permission error!')
        break
      case 401:
        window.fpm.$store.dispatch('auth/logout').then(() => {
          window.fpm.$router.push({ name: 'login' })
        })
        console.warn("WARNING! This code shouldn't fire in production! You should catch all protected pages in router!")
        break
      case 423:
        // eslint-disable-next-line no-case-declarations
        const workspaceStatus = +error.response.data?.errors?.status
        if (!Number.isNaN(workspaceStatus)) {
          if (workspaceStatus === 1) {
            window.fpm.$router.push({ name: 'register-verification' })
          } else if (workspaceStatus === 0) {
            window.fpm.$router.push({ name: 'lock', params: { 0: '' } })
          } else if (workspaceStatus === -1) {
            window.fpm.$appToast.info('Workspace creation in progress!')
          } else {
            throw new Error('Unknown workspace status: ' + workspaceStatus)
          }
        }
        break
    }
  }

  throw error
}

export const get = (url, config = {}) => {
  return HTTP.get(url, config).catch(_handleError)
}

// create new item
// copying data to prevent pass id to the server and not delete id
// in vuex storage (if u know better solution - try it)
export const post = (url, data = {}, config = {}) => {
  if (Object.hasOwn(data, 'id')) {
    data = Object.assign({}, data)
    delete data.id
  }
  return HTTP.post(url, data, config)
    .then((r) => {
      r._isNew = true
      return r
    })
    .catch(_handleError)
}

export const put = (url, data, config) => {
  return HTTP.put(url, data, config)
    .then((r) => {
      r._isNew = false
      return r
    })
    .catch(_handleError)
}

export const destroy = (url, config) => {
  return HTTP.delete(url, config).catch(_handleError)
}

export class Api {
  constructor(entityUrl) {
    this.entityUrl = entityUrl
  }

  save(item, config = {}) {
    if (item) {
      return isLocalId(item.id) ? post(this.entityUrl, item, config) : put(`${this.entityUrl}/${item.id}`, item, config)
    } else throw new Error(`Can't save empty data`)
  }

  getAll(config) {
    return get(this.entityUrl, config).then((r) => {
      let data = {}
      data.items = r.data
      data.pager = pagerExtractor(r.headers)
      r.data = data
      return r
    })
  }

  get({ id, config }) {
    return get(`${this.entityUrl}/${id}`, config)
  }

  destroy({ id, config }) {
    return id && !isLocalId(id) ? destroy(`${this.entityUrl}/${id}`, config) : Promise.resolve()
  }

  getCustom({ urlSuffix, config = {} }) {
    let url = this.entityUrl
    if (urlSuffix) {
      url += `/${urlSuffix}`
    }
    return get(url, config)
  }
}
